import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { formatNumber } from '../utils/formatUtils';
import { trackFilterChange } from '../services/rumService';

type ImpactViewMode = 'per_sqm' | 'total';

interface PortfolioEnvironmentalImpactProps {
  avgCO2PerM2: number;
  totalCO2: number;
  chartData: Array<{
    month: string;
    started: number;
  }>;
  chartDataTotal: Array<{
    month: string;
    started: number;
  }>;
}

const PortfolioEnvironmentalImpact: React.FC<
  PortfolioEnvironmentalImpactProps
> = ({ avgCO2PerM2, totalCO2, chartData, chartDataTotal }) => {
  const [viewMode, setViewMode] = useState<ImpactViewMode>('per_sqm');

  const currentChartData = viewMode === 'per_sqm' ? chartData : chartDataTotal;
  const currentMetricValue = viewMode === 'per_sqm' ? avgCO2PerM2 : totalCO2;
  const currentUnit = viewMode === 'per_sqm' ? 'kg' : 'tons';
  const currentMetricLabel = viewMode === 'per_sqm' ? 'Annual Average CO₂e/m²' : 'Annual Total CO₂e';
  const currentYAxisLabel = viewMode === 'per_sqm' ? 'kg CO₂e/m²' : 'tons CO₂e';

  return (
    <div className="max-w-[1440px] mx-auto pt-[72px]">
      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-[#e9eaeb] shadow-sm">
          <div className="border-b border-[#e9e9e8] px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-[16px] font-bold text-[#696969]">
                Portfolio environmental impact
              </h2>
              <div className="bg-[#f5f6f5] rounded-lg p-0.5 border border-[#e9e9e8]">
                <div className="flex">
                  <button
                    onClick={() => {
                      trackFilterChange('view_mode', viewMode, 'per_sqm', {
                        component: 'PortfolioEnvironmentalImpact',
                      });
                      setViewMode('per_sqm');
                    }}
                    className={`px-2 py-1 text-[14px] font-semibold transition-colors rounded-lg ${
                      viewMode === 'per_sqm'
                        ? 'bg-white text-[#2f2f2f] shadow-sm border border-[#e9e9e8]'
                        : 'text-[#636b63] hover:text-[#2f2f2f]'
                    }`}
                  >
                    kg CO₂e per m²
                  </button>
                  <button
                    onClick={() => {
                      trackFilterChange('view_mode', viewMode, 'total', {
                        component: 'PortfolioEnvironmentalImpact',
                      });
                      setViewMode('total');
                    }}
                    className={`px-2 py-1 text-[14px] font-semibold transition-colors rounded-lg ${
                      viewMode === 'total'
                        ? 'bg-white text-[#2f2f2f] shadow-sm border border-[#e9e9e8]'
                        : 'text-[#636b63] hover:text-[#2f2f2f]'
                    }`}
                  >
                    total tons CO₂e
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 flex items-end gap-8">
            <div className="w-[317px] space-y-6">
              <div className="space-y-2">
                <div className="space-y-0">
                  <div className="text-[16px] font-bold text-[#636b63]">
                    {currentMetricLabel}
                  </div>
                  <div className="text-[40px] font-bold text-[#181d27] tracking-tight">
                    {formatNumber(currentMetricValue)} {currentUnit}
                  </div>
                </div>
                <p className="text-[14px] text-[#636b63] leading-6">
                  Our analysis is based on your projects on Prodikt and the
                  Bim.com Design App. Your impact is measured from LCA life
                  cycle stage A1-A3
                </p>
              </div>
            </div>

            <div
              className="flex-1 h-[280px]"
              style={
                {
                  '--recharts-cursor-fill': 'transparent',
                } as React.CSSProperties
              }
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={currentChartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#636b63' }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#636b63' }}
                    tickFormatter={value => formatNumber(Number(value))}
                    label={{
                      value: currentYAxisLabel,
                      angle: -90,
                      position: 'insideLeft',
                      offset: -10,
                      style: {
                        textAnchor: 'middle',
                        fill: '#636b63',
                        fontSize: '12px',
                      },
                    }}
                  />
                  <CartesianGrid
                    vertical={false}
                    stroke="#e9e9e8"
                    strokeDasharray="3 3"
                    opacity={1}
                  />
                  <Tooltip
                    cursor={false}
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div
                            style={{
                              background: 'rgba(34, 40, 49, 0.98)',
                              borderRadius: 12,
                              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                              padding: '18px 22px 14px 22px',
                              minWidth: 160,
                              color: '#fff',
                              border: '1px solid #1e2a36',
                              fontFamily: 'inherit',
                            }}
                            aria-label={`Tooltip for ${label}`}
                          >
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: 15,
                                marginBottom: 6,
                                letterSpacing: 0.1,
                                color: '#e0e0e0',
                              }}
                            >
                              {label}
                            </div>
                            {payload.map((entry, idx) => (
                              <div
                                key={idx}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  marginTop: idx === 0 ? 0 : 6,
                                }}
                              >
                                <span
                                  style={{
                                    display: 'inline-block',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    background: entry.color,
                                    marginRight: 10,
                                    border: '1.5px solid #fff',
                                  }}
                                />
                                <span
                                  style={{
                                    fontWeight: 500,
                                    fontSize: 14,
                                    color: '#b2dfdb',
                                    marginRight: 8,
                                  }}
                                >
                                  {entry.name}
                                </span>
                                <span
                                  style={{
                                    fontWeight: 700,
                                    fontSize: 15,
                                    color: '#fff',
                                    marginLeft: 'auto',
                                  }}
                                >
                                  {`${formatNumber(
                                    entry.value
                                  )} ${currentUnit}`}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar
                    dataKey="started"
                    fill="#2A654E"
                    name="Started Projects"
                    barSize={18}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioEnvironmentalImpact;
